/**
 * Judgment Selling traits — product sell sheet (not RPG combat stats).
 * Sell copy (name/pitch/causal) is parsed from SSOT markdown — do not duplicate.
 * SSOT: docs/architecture/Game_Proto_Judgment_Selling_기질.md
 */
import {
  judgmentSellHelp,
  type JudgmentSellPitch as SsotSellPitch,
  type JudgmentSellTraitId as SsotSellTraitId,
} from "../../../data/judgment-sell-help";

export const JUDGMENT_SELL_TRAITS = [
  "instinct",
  "insight",
  "bond",
] as const;

export type JudgmentSellTraitId = SsotSellTraitId;

/** Demoted from sell sheet — role floor / survive thr only. */
export type JudgmentInternalTraitId = "grit";

export type JudgmentTraitId = JudgmentSellTraitId | JudgmentInternalTraitId;

export type JudgmentTraits = {
  /** Fast DangerZone reaction — independent of bond. */
  instinct: number;
  /** Independent local optima / initiative without bond. */
  insight: number;
  /** Collective-consciousness assist (rescue/cover), not puppeteer. */
  bond: number;
  /** Demoted — survive HP threshold role floor. */
  grit: number;
};

/**
 * Polar tradeoff profile hooks (SSOT §6–8).
 * Soft≪AA failure modes: `polar.ts` (Sim Lab sell-visible).
 * Extreme ≠ combat coeff buff.
 */
export type JudgmentPolarPole = "low" | "mid" | "high";

export type JudgmentPolarTradeoff = {
  /** Which sell axis this profile polarizes. */
  axis: JudgmentSellTraitId;
  /** Intended pole for roster character fantasy. */
  pole: JudgmentPolarPole;
  /**
   * Human-readable failure mode id (e.g. "abandon_dps_for_dodge").
   * Adapters may later key Soft-visible penalties off this — not coeffs.
   */
  failureModeId?: string;
};

export type JudgmentSellPitch = SsotSellPitch;

/** Sell sheet labels — live view of SSOT trait sections. */
export const JUDGMENT_SELL_SHEET: readonly JudgmentSellPitch[] =
  judgmentSellHelp().sellSheet;

export function clampTrait01to100(v: number): number {
  if (!Number.isFinite(v)) return 50;
  return Math.max(0, Math.min(100, v));
}

/** Centered norm: 0→−1, 50→0, 100→+1. */
export function judgmentNorm(axis: number): number {
  return Math.max(-1, Math.min(1, (clampTrait01to100(axis) - 50) / 50));
}
